import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, num, dt } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { ReceiveModal, MaterialModal } from './RawMaterials';

const txnBadge = (t) => {
  if (['purchase', 'production_yield', 'transfer_in', 'opening', 'return'].includes(t)) return 'badge-green';
  if (['production_consume', 'sale', 'sale_online', 'transfer_out', 'damage', 'expiry'].includes(t)) return 'badge-red';
  return 'badge-gray';
};

const RawMaterialDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiving, setReceiving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/raw-materials/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => { toast.error('Raw material not found'); navigate('/raw-materials'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(load, [load]);
  useEffect(() => { api.get('/suppliers').then(({ data }) => setSuppliers(data.suppliers || [])).catch(() => {}); }, []);

  if (loading) return <div className="spinner" />;
  if (!data) return null;
  const { material: m, stock, totalQty, totalValue, ledger, whereUsed, madeBy = [] } = data;

  return (
    <div className="prod-detail">
      <div className="tabs">
        <span className="tab active">Raw Material</span>
        <div className="tabs-actions">
          {can('rawmaterials:write') && <button className="btn btn-primary btn-sm" onClick={() => setReceiving(true)}>Receive stock</button>}
          {can('rawmaterials:write') && <button className="btn btn-outline btn-sm" onClick={() => setEditing(true)}>Edit</button>}
        </div>
      </div>

      <div className="prod-head">
        <div className="prod-head-main">
          <h1>{m.name}{!m.isActive && <span className="badge badge-gray" style={{ marginLeft: '.6rem' }}>Inactive</span>}</h1>
          <div className="muted">{m.kind} · unit {m.unit}{m.sku ? ` · SKU ${m.sku}` : ''}{m.supplier ? ` · ${m.supplier.name}` : ''}</div>
          {madeBy.length > 0 && (
            <div className="muted small" style={{ marginTop: '.3rem' }}>
              Made in-house from {madeBy.map((r, i) => <React.Fragment key={r.id}>{i ? ', ' : ''}<Link to={`/recipes/${r.id}`} className="link-strong">{r.name}</Link></React.Fragment>)} — make it from the Making page (+ Make raw material).
            </div>
          )}
        </div>
      </div>

      <div className="stat-row">
        <div className="card card-pad stat"><div className="stat-label">On hand</div><div className="stat-value">{num(totalQty)} {m.unit}</div></div>
        <div className="card card-pad stat"><div className="stat-label">Avg cost / unit</div><div className="stat-value">{inr(m.purchasePrice)}</div></div>
        <div className="card card-pad stat"><div className="stat-label">Stock value</div><div className="stat-value">{inr(totalValue)}</div></div>
        <div className="card card-pad stat"><div className="stat-label">Min stock</div><div className={`stat-value ${totalQty < Number(m.minStock) ? 'warn' : ''}`}>{num(m.minStock)} {m.unit}</div></div>
      </div>

      <div className="attr-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="card card-pad">
          <h3 style={{ fontSize: '.95rem', marginBottom: '.6rem' }}>Stock by location</h3>
          <table className="attr-table">
            <tbody>
              {stock.length === 0 ? <tr><td className="muted center">No stock on hand.</td></tr>
                : stock.map((s, i) => <tr key={i}><th>{s.locationName}</th><td>{num(s.quantity)} {s.unit}</td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="card card-pad">
          <h3 style={{ fontSize: '.95rem', marginBottom: '.6rem' }}>Used in recipes</h3>
          <table className="attr-table">
            <tbody>
              {whereUsed.length === 0 ? <tr><td className="muted center">Not used in any recipe.</td></tr>
                : whereUsed.map((w, i) => (
                  <tr key={i}><th><Link to={`/recipes/${w.recipeId}`} className="link-strong">{w.recipe}</Link>{w.product ? <span className="muted"> · {w.product}</span> : null}</th><td>{num(w.quantity)} {w.unit}</td></tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <h3 style={{ fontSize: '.95rem', margin: '1.2rem 0 .5rem' }}>Stock movements</h3>
      <div className="table-wrap">
        <table className="grid">
          <thead><tr><th>When</th><th>Type</th><th>Location</th><th className="right">Qty</th><th className="right">Unit cost</th><th>Ref</th><th>Note</th></tr></thead>
          <tbody>
            {ledger.map((e) => (
              <tr key={e.id}>
                <td>{dt(e.createdAt)}</td>
                <td><span className={`badge ${txnBadge(e.txnType)}`}>{e.txnType.replace(/_/g, ' ')}</span></td>
                <td>{e.location}</td>
                <td className={`right ${e.quantity < 0 ? 'neg' : 'pos'}`}>{e.quantity > 0 ? '+' : ''}{num(e.quantity)} {e.unit}</td>
                <td className="right">{e.unitCost != null ? inr(e.unitCost) : '—'}</td>
                <td>{e.refType ? `${e.refType}#${e.refId ?? ''}` : '—'}</td>
                <td className="muted">{e.note || '—'}</td>
              </tr>
            ))}
            {ledger.length === 0 && <tr><td colSpan={7} className="empty">No movements yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '1rem' }}><Link to="/raw-materials" className="btn btn-outline btn-sm">← Back to Raw Materials</Link></div>

      {receiving && <ReceiveModal material={m} onClose={() => setReceiving(false)} onDone={() => { setReceiving(false); load(); }} />}
      {editing && <MaterialModal material={m} suppliers={suppliers} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} onAddSupplier={async () => null} />}
    </div>
  );
};

export default RawMaterialDetail;
